import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, User, FileText, MessageSquare, TrendingUp, Route, Briefcase,
  Sparkles, LogOut, Crown, Award, Trophy, Users, Mic, Handshake, PiggyBank,
  Settings as SettingsIcon, Gift, Shield, ChevronLeft, ChevronRight,
  Brain, Building2, Palette, LifeBuoy, Mail,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import BrandMark from "@/components/BrandMark";
import SocialLinks from "@/components/SocialLinks";

const groups = [
  {
    title: "Overview",
    links: [
      { to: "/app", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
      { to: "/app/insights", label: "Insights", icon: Sparkles, testId: "nav-insights" },
      { to: "/app/achievements", label: "Achievements", icon: Trophy, testId: "nav-achievements" },
    ],
  },
  {
    title: "Growth",
    links: [
      { to: "/app/chat", label: "Career Advisor", icon: MessageSquare, testId: "nav-chat" },
      { to: "/app/roadmap", label: "Roadmap", icon: Route, testId: "nav-roadmap" },
      { to: "/app/salary", label: "Salary Intel", icon: TrendingUp, testId: "nav-salary" },
      { to: "/app/assessments", label: "Assessments", icon: Brain, testId: "nav-assessments" },
      { to: "/app/executive", label: "Executive", icon: Crown, testId: "nav-executive" },
      { to: "/app/financial", label: "Financial", icon: PiggyBank, testId: "nav-financial" },
    ],
  },
  {
    title: "Hunt",
    links: [
      { to: "/app/jobs", label: "Jobs", icon: Briefcase, testId: "nav-jobs" },
      { to: "/app/network", label: "Network", icon: Users, testId: "nav-network" },
      { to: "/app/interview", label: "Interview", icon: Mic, testId: "nav-interview" },
      { to: "/app/interview/live", label: "Live Voice", icon: Mic, testId: "nav-interview-live", badge: "NEW" },
      { to: "/app/negotiation", label: "Negotiation", icon: Handshake, testId: "nav-negotiation" },
    ],
  },
  {
    title: "Assets",
    links: [
      { to: "/app/profile", label: "Profile", icon: User, testId: "nav-profile" },
      { to: "/app/resume", label: "Resume AI", icon: FileText, testId: "nav-resume" },
      { to: "/app/portfolio", label: "Portfolio", icon: Palette, testId: "nav-portfolio" },
      { to: "/app/certifications", label: "Certifications", icon: Award, testId: "nav-certifications" },
    ],
  },
  {
    title: "Recruit",
    links: [
      { to: "/app/recruiter", label: "Recruiter Portal", icon: Building2, testId: "nav-recruiter" },
    ],
  },
  {
    title: "Account",
    links: [
      { to: "/app/referrals", label: "Referrals", icon: Gift, testId: "nav-referrals" },
      { to: "/app/support", label: "Help & Support", icon: LifeBuoy, testId: "nav-support" },
      { to: "/app/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
    ],
  },
];

export default function Sidebar({ collapsed, setCollapsed, variant = "desktop" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); nav("/"); };

  // Mobile drawer needs a fully opaque background with a subtle blur so the
  // navigation text has maximum contrast against whatever page content sits
  // behind the drawer. Desktop keeps the existing translucent card.
  const isMobile = variant === "mobile";
  const containerBg = isMobile
    ? "bg-background/98 backdrop-blur-xl shadow-2xl"
    : "bg-card/40";

  return (
    <TooltipProvider delayDuration={80}>
      <aside
        data-testid={isMobile ? "sidebar-mobile" : "sidebar"}
        className={`h-screen sticky top-0 border-r border-border ${containerBg} flex flex-col shrink-0 transition-[width] duration-300`}
        style={{ width: collapsed ? "var(--sidebar-width-collapsed)" : "var(--sidebar-width)" }}
      >
        <div className="h-14 px-4 border-b border-border flex items-center justify-between shrink-0">
          <Link to="/app" className="flex items-center gap-2.5 min-w-0" data-testid="sidebar-logo">
            <BrandMark size={32} className="rounded-lg" />
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-foreground tracking-tight leading-none">Career OS</div>
                <div className="text-[9px] text-muted-foreground mt-0.5 font-mono uppercase tracking-widest">v2.0</div>
              </div>
            )}
          </Link>
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-collapse-btn"
            className="hidden md:flex w-6 h-6 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {progress && !collapsed && (
          <Link
            to="/app/achievements"
            data-testid="sidebar-level"
            className="mx-3 mt-3 border border-border rounded-lg p-3 hover:border-primary/50 transition-colors block"
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Level</div>
              <div className="text-[10px] font-mono text-muted-foreground">{progress.xp} XP</div>
            </div>
            <div className="font-display font-black text-2xl mt-0.5 text-foreground">{progress.level}</div>
            <div className="h-1.5 bg-secondary rounded-full mt-2 overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress.progress_pct || 0}%` }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </Link>
        )}

        <nav className="flex-1 px-2 py-3 space-y-4 overflow-y-auto">
          {groups.map((g) => (
            <div key={g.title}>
              {!collapsed && (
                <div className="px-3 mb-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                  {g.title}
                </div>
              )}
              <div className="space-y-0.5">
                {g.links.map((l) => (
                  <NavItem key={l.to} link={l} collapsed={collapsed} />
                ))}
              </div>
            </div>
          ))}
          {user?.is_admin && (
            <>
              <NavItem
                collapsed={collapsed}
                link={{ to: "/app/admin", label: "Admin", icon: Shield, testId: "nav-admin" }}
                extraClass="text-[hsl(var(--warning))]"
              />
              <NavItem
                collapsed={collapsed}
                link={{ to: "/app/admin/support", label: "Admin · Support", icon: LifeBuoy, testId: "nav-admin-support" }}
                extraClass="text-[hsl(var(--warning))]"
              />
              <NavItem
                collapsed={collapsed}
                link={{ to: "/app/admin/email", label: "Admin · Email", icon: Mail, testId: "nav-admin-email" }}
                extraClass="text-[hsl(var(--warning))]"
              />
            </>
          )}

          {/* Follow-us socials — sits directly below the last nav tab
              (Settings, or Admin · Email for admin users). Icons only, no
              text/URLs; hover glows with the CareerOS primary accent. */}
          <div
            className={`pt-3 mt-2 border-t border-border/50 flex ${collapsed ? "flex-col items-center gap-1" : "justify-start px-3"}`}
          >
            <SocialLinks
              testId="sidebar-social-links"
              size={collapsed ? 14 : 15}
              className={collapsed ? "flex-col gap-1" : ""}
            />
          </div>
        </nav>

        <div className="border-t border-border p-2 space-y-1 shrink-0">
          {(() => {
            const tier = String(user?.subscription_tier || "free").toLowerCase();
            if (tier === "free") {
              return (
                <Link
                  to="/app/pricing"
                  data-testid="sidebar-upgrade"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                  title="Upgrade to Pro"
                >
                  <Crown size={15} />
                  {!collapsed && <span>Upgrade to Pro</span>}
                </Link>
              );
            }
            // Paid tier — surface a "Manage plan" pointer instead of the
            // misleading upgrade CTA. Deep-links straight to the billing tab.
            return (
              <Link
                to="/app/settings#billing"
                data-testid="sidebar-manage-plan"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
                title="Manage plan"
              >
                <Crown size={15} className="opacity-80" />
                {!collapsed && <span>Manage plan</span>}
              </Link>
            );
          })()}
          <div className="px-2 py-2 flex items-center gap-2 rounded-lg hover:bg-accent transition-colors">
            <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-foreground text-xs font-bold font-mono shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-foreground truncate">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate font-mono">{user?.email}</div>
                </div>
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-accent transition-colors"
                  aria-label="Log out"
                >
                  <LogOut size={13} />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}

function NavItem({ link, collapsed, extraClass = "" }) {
  const link_el = (
    <NavLink
      to={link.to}
      end={link.end}
      data-testid={link.testId}
      className={({ isActive }) =>
        `group relative flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          isActive
            ? "bg-primary/10 text-primary"
            : `text-muted-foreground hover:text-foreground hover:bg-accent ${extraClass}`
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div
              layoutId="sidebar-active"
              className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-primary"
              transition={{ duration: 0.2 }}
            />
          )}
          <link.icon size={16} strokeWidth={1.75} />
          {!collapsed && <span className="truncate">{link.label}</span>}
          {!collapsed && link.badge && (
            <span className="ml-auto text-[9px] font-mono uppercase tracking-widest bg-primary/15 text-primary px-1.5 py-0.5 rounded">
              {link.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
  if (!collapsed) return link_el;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link_el}</TooltipTrigger>
      <TooltipContent side="right">{link.label}</TooltipContent>
    </Tooltip>
  );
}
